// Subscription Settings Tab
import React, { useState } from "react";
import axios from "axios";
import SettingsSaveButtons from "./SettingsSaveButtons";

const SubscriptionSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
  setMessage,
}) => {
  const [migrateExisting, setMigrateExisting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  // The per-professional migration shows a real dialog instead of window.confirm, so the operator can
  // read every shop's before/after price before anything is written.
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationResult, setMigrationResult] = useState("");
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const [bootstrappingWebhooks, setBootstrappingWebhooks] = useState(false);
  const [webhookBootstrapResult, setWebhookBootstrapResult] = useState(null);
  const [recoveringRoleSubscriptions, setRecoveringRoleSubscriptions] =
    useState(false);
  const [roleRecoveryResult, setRoleRecoveryResult] = useState(null);
  const [recalcRole, setRecalcRole] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  // Handle subscription price update with optional migration
  const handleSubscriptionPriceUpdate = async (e) => {
    if (e) e.preventDefault();

    // Use the parent's saveSettings but with extra subscription-specific logic
    const token = localStorage.getItem("ogestyle_token");

    try {
      // First save to platform settings endpoint
      await saveSettings();

      /**
       * AND ALWAYS SYNC STRIPE (2026-11-01).
       *
       * This used to call `/subscription-prices` ONLY when "Automatically migrate existing customers" was
       * ticked — while the button itself says "Save Prices (New Customers Only)" when it is not. So the one
       * action that promises to save the prices saved the settings document and never created the Stripe price.
       * The migration preview then refused for ever with "Save Prices first — Stripe still holds the old
       * freelancer price", and pressing the button could not clear it.
       *
       * `/subscription-prices` is the route that reconciles against LIVE Stripe (not against the previous
       * settings), and `migrateExistingCustomers: false` means it creates the new prices for new customers
       * without touching anybody's existing subscription. That is exactly what the unchecked button promises.
       */
      const response = await axios.put(
        `${API_BASE_URL}/api/platform-settings/subscription-prices`,
        {
          subscriptionBasePriceMonthly: settings.subscriptionBasePriceMonthly,
          subscriptionAdditionalLocationMonthly:
            settings.subscriptionAdditionalLocationPrice,
          subscriptionFreelancerMonthly:
            settings.subscriptionFreelancerMonthly,
          boothRenterSettings: settings.boothRenterSettings,
          annualDiscountPercent: settings.annualDiscountPercent,
          subscriptionGracePeriodDays: settings.subscriptionGracePeriodDays,
          trialPeriodDays: settings.trialPeriodDays,
          migrationMaxGoLiveScheduleDays:
            settings.migrationMaxGoLiveScheduleDays,
          migrateExistingCustomers: migrateExisting,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const sync = response.data?.stripeSync || {};
      const created = Number(sync.pricesCreated) || 0;
      const migrated = sync.migration?.totalUpdated;

      setMigrationResult(
        [
          created > 0
            ? `Stripe prices updated (${created} new price${created === 1 ? "" : "s"} created).`
            : "Prices saved — Stripe already held these amounts.",
          migrated !== undefined
            ? ` Existing subscriptions migrated: ${migrated}.`
            : " Existing subscriptions were left alone.",
        ].join(""),
      );
    } catch (error) {
      setMigrationResult(
        `Could not save prices${error.response?.status ? ` (HTTP ${error.response.status})` : ""}: ${
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message
        }`,
      );
    }
  };

  // Handle manual migration of existing customers.
  //
  // On the per-professional model this is a DRY RUN first: it lists every shop's current and target
  // price, changes nothing, and only applies if the operator confirms. The legacy model keeps its
  // previous behaviour, because there the call itself performs the migration.
  const handleManualMigration = async () => {
    setMigrating(true);
    const token = localStorage.getItem("ogestyle_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const preview = await axios.post(
        `${API_BASE_URL}/api/platform-settings/subscription-prices/migrate`,
        {},
        { headers },
      );

      const migration = preview.data?.migration;

      if (migration?.mode === "per_professional") {
        setMigrationResult("");
        setMigrationPreview(migration);
        return;
      }

      if (preview.data?.summary) {
        alert(
          `${preview.data.summary} - Business: ${preview.data.migration.businessUpdated}, Freelancer: ${preview.data.migration.freelancerUpdated}`,
        );
      }
    } catch (error) {
      alert(
        "Migration failed" +
          (error.response?.status ? ` (HTTP ${error.response.status})` : "") +
          ": " +
          (error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "unknown error"),
      );
    } finally {
      setMigrating(false);
    }
  };

  /** Apply what the dialog showed. Nothing is written until this runs. */
  const applyMigration = async () => {
    setMigrating(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      const applied = await axios.post(
        `${API_BASE_URL}/api/platform-settings/subscription-prices/migrate`,
        { confirm: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = applied.data?.migration || {};
      setMigrationPreview(null);
      setMigrationResult(
        `${result.message || "Migration done"} Updated: ${result.updated || 0}, failed: ${result.failed || 0}`,
      );
    } catch (error) {
      setMigrationResult(
        `Migration failed${error.response?.status ? ` (HTTP ${error.response.status})` : ""}: ${
          error.response?.data?.message || error.response?.data?.error || error.message
        }`,
      );
    } finally {
      setMigrating(false);
    }
  };

  // Bootstrap all Stripe products and prices from scratch (for empty sandbox or new environment)
  const handleBootstrap = async () => {
    if (
      !window.confirm(
        "This will create Stripe Products and Prices for Business (base + additional), Freelancer, and Booth Renter subscriptions.\n\n" +
          "Existing products/prices with matching metadata will be reused — nothing will be duplicated.\n\n" +
          "Click OK to proceed.",
      )
    ) {
      return;
    }

    setBootstrapping(true);
    setBootstrapResult(null);
    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/platform-settings/subscription-prices/bootstrap`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBootstrapResult({ success: true, data: response.data });
    } catch (error) {
      setBootstrapResult({
        success: false,
        error:
          error.response?.data?.error ||
          "Bootstrap failed. Check the server logs.",
      });
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSubscriptionPriceUpdate(e);
  };

  const handleWebhookBootstrap = async () => {
    if (
      !window.confirm(
        "This will configure Stripe webhook endpoints for this environment using the server-side STRIPE_SECRET_KEY. Continue?",
      )
    ) {
      return;
    }

    setBootstrappingWebhooks(true);
    setWebhookBootstrapResult(null);

    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/platform-settings/stripe-webhooks/bootstrap`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setWebhookBootstrapResult({ success: true, data: response.data });
    } catch (error) {
      setWebhookBootstrapResult({
        success: false,
        error:
          error.response?.data?.error ||
          error.response?.data?.details ||
          "Failed to configure Stripe webhooks.",
      });
    } finally {
      setBootstrappingWebhooks(false);
    }
  };

  const handleRecoverRoleSubscriptions = async () => {
    if (
      !window.confirm(
        "Recover stuck R1/R4 subscriptions from Stripe now? This verifies Stripe status before any updates.",
      )
    ) {
      return;
    }

    setRecoveringRoleSubscriptions(true);
    setRoleRecoveryResult(null);

    const token = localStorage.getItem("ogestyle_token");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/subscription-new/admin/recover-subscription-sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRoleRecoveryResult(response.data);
    } catch (error) {
      setRoleRecoveryResult({
        success: false,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to run subscription recovery",
      });
    } finally {
      setRecoveringRoleSubscriptions(false);
    }
  };

  const handleRecalcVisibility = async (role) => {
    if (!window.confirm(`Recalculate isPubliclyVisible for all ${role} records? This may take a moment.`)) return;
    setRecalcRole(role);
    setRecalcResult(null);
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/recalculate-visibility?role=${role}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRecalcResult({ ...response.data, role });
    } catch (error) {
      setRecalcResult({
        success: false,
        role,
        message: error.response?.data?.error || error.response?.data?.message || "Failed",
      });
    } finally {
      setRecalcRole(null);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Role Subscription Recovery Tool */}
      <div
        style={{
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "24px",
        }}
      >
        <h3 style={{ margin: "0 0 8px", color: "#1e40af", fontSize: "16px" }}>
          🔧 Recover R1/R4 Subscription Sync
        </h3>
        <p style={{ margin: "0 0 12px", color: "#1e3a8a", fontSize: "13px" }}>
          Repairs stuck subscription states for R1 businesses and R4 freelancers.
          Recovery is verification-based and only applies when Stripe reports
          <strong> active </strong>or<strong> trialing</strong>.
        </p>
        <button
          type="button"
          onClick={handleRecoverRoleSubscriptions}
          disabled={recoveringRoleSubscriptions}
          style={{
            background: recoveringRoleSubscriptions ? "#93c5fd" : "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "10px 16px",
            cursor: recoveringRoleSubscriptions ? "not-allowed" : "pointer",
            fontWeight: 600,
            fontSize: "13px",
          }}
        >
          {recoveringRoleSubscriptions
            ? "Recovering..."
            : "Recover R1/R4 Subscriptions"}
        </button>

        {roleRecoveryResult && (
          <div
            style={{
              marginTop: "12px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "12px",
            }}
          >
            {roleRecoveryResult.success ? (
              <>
                <div style={{ marginBottom: "8px", fontWeight: 600 }}>
                  Processed: {roleRecoveryResult.processed} | Recovered: {roleRecoveryResult.recovered}
                </div>
                {(roleRecoveryResult.results || []).slice(0, 10).map((row, idx) => (
                  <div key={idx} style={{ marginBottom: "4px" }}>
                    {row.role} {String(row.entityId)}: {row.outcome}
                    {row.stripeStatus ? ` (${row.stripeStatus})` : ""}
                  </div>
                ))}
              </>
            ) : (
              <div style={{ color: "#b91c1c" }}>
                {roleRecoveryResult.message || "Recovery failed"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recalculate Visibility Tool */}
      <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
        <h3 style={{ margin: "0 0 8px", color: "#15803d", fontSize: "16px" }}>
          👁️ Recalculate Visibility (R1 / R3 / R4)
        </h3>
        <p style={{ margin: "0 0 12px", color: "#166534", fontSize: "13px" }}>
          Recomputes the <strong>isPubliclyVisible</strong> flag for all records of the selected role. Use after deploying visibility gate changes or when a role appears missing from public pages.
        </p>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {["R1", "R3", "R4"].map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => handleRecalcVisibility(role)}
              disabled={!!recalcRole}
              style={{
                background: recalcRole === role ? "#d1d5db" : "#16a34a",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: "13px",
                cursor: recalcRole ? "not-allowed" : "pointer",
              }}
            >
              {recalcRole === role ? `Recalculating ${role}…` : `Recalculate ${role}`}
            </button>
          ))}
        </div>
        {recalcResult && (
          <div style={{ marginTop: "12px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px", fontSize: "12px" }}>
            {recalcResult.success === false ? (
              <div style={{ color: "#b91c1c" }}>{recalcResult.message || "Failed"}</div>
            ) : (
              <>
                <div style={{ fontWeight: 600, marginBottom: "6px" }}>
                  {recalcResult.role}: {recalcResult.visible} of {recalcResult.total} now publicly visible
                </div>
                {(recalcResult.results || []).filter((r) => r.error || (r.failedChecks && r.failedChecks.length > 0)).slice(0, 10).map((r, i) => (
                  <div key={i} style={{ color: "#6b7280", marginBottom: "2px" }}>
                    ⚠️ {r.name || r.slug || String(r.id)}: {r.error || (r.failedChecks || []).join(", ")}
                  </div>
                ))}
                {recalcResult.visible === recalcResult.total && recalcResult.total > 0 && (
                  <div style={{ color: "#16a34a" }}>✅ All {recalcResult.role} records are publicly visible.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Trial & Subscription Settings Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          🎁 Trial Period & Subscription Model
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure trial duration and subscription pricing. Trial users pay
          zero fees.
        </p>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Trial Period (Days)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="90"
            value={settings.trialPeriodDays ?? 0}
            onChange={(e) =>
              updateSettings({ trialPeriodDays: parseInt(e.target.value) })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Duration of free trial for new locations (0-90 days). During trial,
            all fees are 0%.
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Max Go-Live Schedule Window (Days)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="90"
            value={settings.migrationMaxGoLiveScheduleDays ?? 0}
            onChange={(e) =>
              updateSettings({
                migrationMaxGoLiveScheduleDays: Number.isNaN(Number(e.target.value))
                  ? 0
                  : Number(e.target.value),
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Maximum number of days users can schedule migration go-live in advance (1-90 days).
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Subscription Pricing Model
          </label>
          <select
            value={settings.subscriptionPricingModel ?? "base_plus_location"}
            onChange={(e) =>
              updateSettings({ subscriptionPricingModel: e.target.value })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            <option value="per_business">
              Per Business (Flat fee per business)
            </option>
            <option value="per_location">
              Per Location (Fee for each location)
            </option>
            <option value="base_plus_location">
              Base + Location (First location + additional locations)
            </option>
            <option value="per_professional">
              Per Professional (Per active R3 at each location, capped)
            </option>
          </select>
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            How subscription pricing is calculated for multi-location
            businesses.
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Booth Renter Fee for Businesses ($/month)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={
              settings.boothRenterSettings?.businessMonthlyFeePerRenter ?? ""
            }
            onChange={(e) =>
              updateSettings({
                boothRenterSettings: {
                  ...settings.boothRenterSettings,
                  businessMonthlyFeePerRenter:
                    e.target.value === "" ? "" : parseFloat(e.target.value),
                },
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Monthly fee charged to businesses for each active booth renter. This
            value is used by billing and invoices.
          </p>
        </div>

        {settings.subscriptionPricingModel === "per_professional" && (
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              padding: "16px",
              marginBottom: "24px",
              background: "#f8fafc",
            }}
          >
            <h3 style={{ marginTop: 0, fontSize: "16px" }}>
              Per-professional plan (X and Y)
            </h3>
            <p style={{ fontSize: "12px", color: "#6b7280", marginTop: 0 }}>
              Priced per location: professionals below the threshold cost the per-professional rate
              each; from the threshold up the location costs the flat cap. Never below the smaller
              charge.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                  Per professional ($/month)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.perProfessionalRate ?? ""}
                  onChange={(e) =>
                    updateSettings({
                      perProfessionalRate: e.target.value === "" ? "" : parseFloat(e.target.value),
                    })
                  }
                  style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                  Flat cap per location ($/month)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.perLocationCap ?? ""}
                  onChange={(e) =>
                    updateSettings({
                      perLocationCap: e.target.value === "" ? "" : parseFloat(e.target.value),
                    })
                  }
                  style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                  Threshold (professionals)
                </label>
                <input
                  type="number"
                  step="1"
                  min="2"
                  value={settings.capThreshold ?? ""}
                  onChange={(e) =>
                    updateSettings({
                      capThreshold: e.target.value === "" ? "" : parseInt(e.target.value, 10),
                    })
                  }
                  style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                />
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            padding: "16px",
            marginBottom: "24px",
            background: "#f8fafc",
          }}
        >
          <h3 style={{ marginTop: 0, fontSize: "16px" }}>Promos &amp; referral agents</h3>
          <p style={{ fontSize: "12px", color: "#6b7280", marginTop: 0 }}>
            Commission rates, the cap and the payout minimum. New agents snapshot whatever is set
            here when they are approved, so changing a rate does not rewrite an existing agreement.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                Agent commission (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.agentCommissionPercent ?? ""}
                onChange={(e) =>
                  updateSettings({
                    agentCommissionPercent: e.target.value === "" ? "" : parseFloat(e.target.value),
                  })
                }
                style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                R1/R4 partner commission (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={settings.partnerCommissionPercent ?? ""}
                onChange={(e) =>
                  updateSettings({
                    partnerCommissionPercent:
                      e.target.value === "" ? "" : parseFloat(e.target.value),
                  })
                }
                style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                Commission cap (months)
              </label>
              <input
                type="number"
                step="1"
                min="1"
                value={settings.agentCommissionCapMonths ?? ""}
                onChange={(e) =>
                  updateSettings({
                    agentCommissionCapMonths:
                      e.target.value === "" ? "" : parseInt(e.target.value, 10),
                  })
                }
                style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontWeight: "500", marginBottom: "6px" }}>
                Minimum referral payout ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={
                  settings.payoutSettings?.referralMinimumPayoutCents === undefined ||
                  settings.payoutSettings?.referralMinimumPayoutCents === null
                    ? ""
                    : settings.payoutSettings.referralMinimumPayoutCents / 100
                }
                onChange={(e) =>
                  updateSettings({
                    payoutSettings: {
                      ...settings.payoutSettings,
                      referralMinimumPayoutCents:
                        e.target.value === "" ? "" : Math.round(parseFloat(e.target.value) * 100),
                    },
                  })
                }
                style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
              />
            </div>
          </div>
        </div>

        {/*
          The flat business prices only exist for the old models. On the per-professional model a price is
          derived from each location's seats, so leaving these editable invites someone to set a number
          that is never used.
        */}
        {settings.subscriptionPricingModel === "base_plus_location" && (
          <>
            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Base Price (First Location) ($/month)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                max="1000"
                value={settings.subscriptionBasePriceMonthly ?? 35}
                onChange={(e) =>
                  updateSettings({
                    subscriptionBasePriceMonthly: parseInt(e.target.value),
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
                required
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Monthly subscription for the first/primary location.
              </p>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Additional Location Price ($/month)
              </label>
              <input
                type="number"
                step="1"
                min="0"
                max="500"
                value={settings.subscriptionAdditionalLocationPrice ?? 25}
                onChange={(e) =>
                  updateSettings({
                    subscriptionAdditionalLocationPrice: parseInt(
                      e.target.value,
                    ),
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
                required
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Monthly price for each additional location beyond the first.
              </p>
            </div>

            <div
              style={{
                padding: "16px",
                background: "#eff6ff",
                borderRadius: "8px",
                marginBottom: "24px",
              }}
            >
              <strong style={{ color: "#1e40af" }}>💡 Pricing Examples:</strong>
              <ul
                style={{
                  margin: "8px 0 0",
                  paddingLeft: "20px",
                  color: "#1e40af",
                  fontSize: "13px",
                }}
              >
                <li>
                  1 location: ${settings.subscriptionBasePriceMonthly ?? 35}
                  /month
                </li>
                <li>
                  2 locations: $
                  {(settings.subscriptionBasePriceMonthly ?? 35) +
                    (settings.subscriptionAdditionalLocationPrice ?? 25)}
                  /month
                </li>
                <li>
                  3 locations: $
                  {(settings.subscriptionBasePriceMonthly ?? 35) +
                    (settings.subscriptionAdditionalLocationPrice ?? 25) * 2}
                  /month
                </li>
                <li>
                  5 locations: $
                  {(settings.subscriptionBasePriceMonthly ?? 35) +
                    (settings.subscriptionAdditionalLocationPrice ?? 25) * 4}
                  /month
                </li>
              </ul>
            </div>
          </>
        )}

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Freelancer Subscription ($/month)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="1000"
            value={settings.subscriptionFreelancerMonthly ?? 25}
            onChange={(e) =>
              updateSettings({
                subscriptionFreelancerMonthly: parseInt(e.target.value),
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Monthly subscription for freelancers. Services: 0% fee, Products: 5%
            fee always.
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Annual Discount (%)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="100"
            value={settings.annualDiscountPercent ?? 15}
            onChange={(e) =>
              updateSettings({
                annualDiscountPercent: parseInt(e.target.value),
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Discount percentage for annual subscriptions (e.g., 15 for 15% off).
          </p>
        </div>

        <div style={{ marginBottom: "24px" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Grace Period (Days)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="14"
            value={settings.subscriptionGracePeriodDays ?? 3}
            onChange={(e) =>
              updateSettings({
                subscriptionGracePeriodDays: parseInt(e.target.value),
              })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Days after failed subscription payment before reverting to
            usage-based billing.
          </p>
        </div>

        <div style={{ marginBottom: "0" }}>
          <label
            style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}
          >
            Model Switch Lock (Days)
          </label>
          <input
            type="number"
            step="1"
            min="0"
            max="90"
            value={settings.modelSwitchLockDays ?? 30}
            onChange={(e) =>
              updateSettings({ modelSwitchLockDays: parseInt(e.target.value) })
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            }}
            required
          />
          <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0" }}>
            Days user must wait after switching payment models before switching
            again.
          </p>
        </div>

        {/* Advanced Subscription Management */}
        <div
          style={{
            marginTop: "32px",
            paddingTop: "32px",
            borderTop: "2px solid #e5e7eb",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            🔄 Advanced: Stripe Price Sync & Migration
          </h3>

          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            {settings.subscriptionPricingModel !== "per_professional" && (
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={migrateExisting}
                    onChange={(e) => setMigrateExisting(e.target.checked)}
                    style={{
                      marginRight: "8px",
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />
                  Automatically migrate existing customers when saving
                </label>
              </div>
            )}
            {settings.subscriptionPricingModel === "per_professional" && (
              <p style={{ fontSize: "13px", color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", lineHeight: 1.5 }}>
                <strong>Switched off on the per-professional model.</strong> Saving would otherwise push
                every business onto the old flat price. Use <em>Migrate Existing Customers Now</em> below —
                it recalculates each shop from its seats and changes nothing until you confirm.
              </p>
            )}

            <p
              style={{
                fontSize: "12px",
                color: "#6b7280",
                marginBottom: "12px",
                lineHeight: "1.5",
              }}
            >
              {migrateExisting ? (
                <span>
                  ✅ <strong>Enabled:</strong> When you save, both new AND
                  existing customers will use the new prices. All active
                  subscriptions will be updated immediately.
                </span>
              ) : (
                <span>
                  ⏸️ <strong>Disabled:</strong> New prices apply to new
                  customers only. Existing customers keep their current prices
                  until you manually migrate them below.
                </span>
              )}
            </p>

            <button
              type="button"
              onClick={handleSubscriptionPriceUpdate}
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: migrateExisting ? "#dc2626" : "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving
                ? "Updating..."
                : migrateExisting
                  ? "💾 Save & Migrate All Customers"
                  : "💾 Save Prices (New Customers Only)"}
            </button>
          </div>

          {/* Manual Migration Button */}
          {!migrateExisting && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  color: "#92400e",
                  marginBottom: "12px",
                  fontWeight: "500",
                }}
              >
                ⚠️ Manual Migration Available
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#78350f",
                  marginBottom: "12px",
                  lineHeight: "1.5",
                }}
              >
                You have saved new prices, but existing customers are still on
                old prices. Use this button to update all active subscriptions
                to current pricing.
              </p>
              <button
                type="button"
                onClick={handleManualMigration}
                disabled={migrating}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  cursor: migrating ? "not-allowed" : "pointer",
                  opacity: migrating ? 0.7 : 1,
                }}
              >
                {migrating
                  ? "Migrating..."
                  : "🔄 Migrate Existing Customers Now"}
              </button>

              {migrationResult && (
                <div
                  style={{
                    marginTop: "12px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    color: "#166534",
                    borderRadius: "8px",
                    padding: "10px 12px",
                    fontSize: "13px",
                  }}
                >
                  {migrationResult}
                </div>
              )}

              {migrationPreview && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(15,23,42,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    padding: "20px",
                  }}
                  onClick={() => setMigrationPreview(null)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: "#ffffff",
                      borderRadius: "12px",
                      width: "100%",
                      maxWidth: "720px",
                      maxHeight: "85vh",
                      overflow: "auto",
                      padding: "20px",
                      boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
                    }}
                  >
                    <h3 style={{ margin: "0 0 6px", fontSize: "17px" }}>
                      Migrate existing subscriptions?
                    </h3>
                    <p style={{ margin: "0 0 14px", fontSize: "13px", color: "#6b7280" }}>
                      Nothing has been changed yet. These are the prices each shop pays now and what
                      it will pay after migration. Booth-renter lines are left alone.
                    </p>

                    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr 1fr 1fr",
                          background: "#f8fafc",
                          padding: "8px 10px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#475569",
                        }}
                      >
                        <span>Shop</span>
                        <span>Seats</span>
                        <span>Now</span>
                        <span>After</span>
                      </div>
                      {(migrationPreview.rows || []).map((row, idx) => (
                        <div
                          key={row.businessId || row.staffId || idx}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr 1fr 1fr",
                            padding: "8px 10px",
                            fontSize: "13px",
                            borderTop: "1px solid #f1f5f9",
                            color: row.error ? "#b91c1c" : "#111827",
                          }}
                        >
                          <span>{row.name}</span>
                          <span>{row.error || row.skipped ? "—" : row.seats}</span>
                          <span>{row.error ? "—" : `${((row.currentCents || 0) / 100).toFixed(2)}`}</span>
                          <span>
                            {row.error ? (
                              row.error
                            ) : row.skipped || row.needsReview ? (
                              // Not migrated, and why. Showing "0.00" here read as "we will cancel this
                              // subscription", which is exactly what must not happen to an R4's own plan.
                              <span style={{ color: "#b45309" }}>{row.note || row.reason}</span>
                            ) : (
                              `${((row.targetCents || 0) / 100).toFixed(2)}`
                            )}
                            {!row.error && row.manualReview ? (
                              <span style={{ color: "#b45309" }}> {row.manualReview}</span>
                            ) : null}
                            {!row.error && row.protectedCents ? (
                              <span style={{ color: "#6b7280" }}>
                                {" "}
                                +${(row.protectedCents / 100).toFixed(2)} untouched
                              </span>
                            ) : null}
                          </span>
                        </div>
                      ))}
                      {(migrationPreview.rows || []).length === 0 && (
                        <div style={{ padding: "10px", fontSize: "13px", color: "#6b7280" }}>
                          No shops with a subscription to migrate.
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                      <button
                        type="button"
                        onClick={() => setMigrationPreview(null)}
                        style={{
                          padding: "9px 14px",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: "8px",
                          fontSize: "13px",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={applyMigration}
                        disabled={migrating}
                        style={{
                          padding: "9px 16px",
                          background: "#f59e0b",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: migrating ? "not-allowed" : "pointer",
                          opacity: migrating ? 0.7 : 1,
                        }}
                      >
                        {migrating ? "Applying..." : "Apply migration"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <p
            style={{
              fontSize: "11px",
              color: "#9ca3af",
              marginTop: "12px",
              lineHeight: "1.5",
            }}
          >
            💡 <strong>How it works:</strong> Stripe prices are immutable. When
            you change prices, new Stripe Price objects are created
            automatically. Old prices remain active for existing customers
            (grandfathering) unless you choose to migrate them.
          </p>

          {/* Stripe Sandbox Bootstrap Panel */}
          <div
            style={{
              marginTop: "24px",
              padding: "20px",
              background: "#f0fdf4",
              border: "1px solid #86efac",
              borderRadius: "8px",
            }}
          >
            <h4
              style={{
                fontSize: "15px",
                fontWeight: "600",
                marginBottom: "4px",
                color: "#14532d",
              }}
            >
              🔧 Stripe Price Model Status
            </h4>
            <p
              style={{
                fontSize: "12px",
                color: "#166534",
                marginBottom: "16px",
              }}
            >
              Use Bootstrap to auto-create all Stripe products and prices in a
              new or empty environment (sandbox or production). Existing items
              are reused — nothing is duplicated.
            </p>

            {/* Price ID status grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginBottom: "16px",
                fontSize: "12px",
              }}
            >
              {[
                {
                  label: "Business Monthly (Base)",
                  key: "stripeBusinessMonthlyPriceId",
                },
                {
                  label: "Business Monthly (Additional)",
                  key: "stripeBusinessMonthlyAdditionalPriceId",
                },
                {
                  label: "Business Annual (Base)",
                  key: "stripeBusinessAnnualPriceId",
                },
                {
                  label: "Business Annual (Additional)",
                  key: "stripeBusinessAnnualAdditionalPriceId",
                },
                {
                  label: "Freelancer Monthly",
                  key: "stripeFreelancerMonthlyPriceId",
                },
                {
                  label: "Freelancer Annual",
                  key: "stripeFreelancerAnnualPriceId",
                },
                {
                  label: "Booth Renter Monthly",
                  key: "stripeBoothRenterMonthlyPriceId",
                },
                {
                  label: "Booth Renter Annual",
                  key: "stripeBoothRenterAnnualPriceId",
                },
              ].map(({ label, key }) => {
                const val = settings[key];
                return (
                  <div
                    key={key}
                    style={{
                      padding: "8px 12px",
                      background: "white",
                      border: `1px solid ${val ? "#86efac" : "#fca5a5"}`,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontSize: "14px" }}>
                      {val ? "✅" : "❌"}
                    </span>
                    <div>
                      <div style={{ fontWeight: "500", color: "#374151" }}>
                        {label}
                      </div>
                      <div
                        style={{
                          color: val ? "#166534" : "#991b1b",
                          fontFamily: "monospace",
                          fontSize: "11px",
                          wordBreak: "break-all",
                        }}
                      >
                        {val || "Not configured"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={handleBootstrap}
              disabled={bootstrapping}
              style={{
                width: "100%",
                padding: "12px",
                background: bootstrapping ? "#6b7280" : "#16a34a",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: bootstrapping ? "not-allowed" : "pointer",
                marginBottom: "8px",
              }}
            >
              {bootstrapping
                ? "⏳ Creating Stripe Products & Prices..."
                : "🚀 Bootstrap Stripe Price Models"}
            </button>

            <button
              type="button"
              onClick={handleWebhookBootstrap}
              disabled={bootstrappingWebhooks}
              style={{
                width: "100%",
                padding: "12px",
                background: bootstrappingWebhooks ? "#6b7280" : "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: bootstrappingWebhooks ? "not-allowed" : "pointer",
                marginBottom: "8px",
              }}
            >
              {bootstrappingWebhooks
                ? "⏳ Configuring Stripe Webhooks..."
                : "🔐 Bootstrap Stripe Webhooks"}
            </button>

            {bootstrapResult && bootstrapResult.success && (
              <div
                style={{
                  padding: "12px",
                  background: "#dcfce7",
                  border: "1px solid #86efac",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#14532d",
                }}
              >
                <strong>✅ Bootstrap complete!</strong>
                <p style={{ margin: "4px 0" }}>
                  Stripe prices are now configured. The page settings have
                  been updated — refresh the page to see the new price IDs.
                </p>
                {bootstrapResult.data?.stripeSync?.dashboardLinks && (
                  <div style={{ marginTop: "8px" }}>
                    <strong>Stripe Dashboard Links:</strong>
                    <ul style={{ margin: "4px 0", paddingLeft: "16px" }}>
                      {Object.entries(
                        bootstrapResult.data.stripeSync.dashboardLinks,
                      ).map(([key, url]) => (
                        <li key={key}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1d4ed8" }}
                          >
                            {key}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {bootstrapResult && !bootstrapResult.success && (
              <div
                style={{
                  padding: "12px",
                  background: "#fee2e2",
                  border: "1px solid #fca5a5",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#991b1b",
                }}
              >
                <strong>❌ Bootstrap failed:</strong> {bootstrapResult.error}
              </div>
            )}

            {webhookBootstrapResult && webhookBootstrapResult.success && (
              <div
                style={{
                  padding: "12px",
                  background: "#dbeafe",
                  border: "1px solid #93c5fd",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#1e3a8a",
                  marginTop: "8px",
                }}
              >
                <strong>✅ Webhook bootstrap complete!</strong>
                <p style={{ margin: "4px 0" }}>
                  Endpoint: {webhookBootstrapResult.data?.endpointUrl}
                </p>
                <p style={{ margin: "4px 0" }}>
                  Standard endpoint ID: {webhookBootstrapResult.data?.standard?.id}
                </p>
                <p style={{ margin: "4px 0" }}>
                  Connect endpoint ID: {webhookBootstrapResult.data?.connect?.id}
                </p>
                <p style={{ margin: "4px 0", fontWeight: 600 }}>
                  Copy returned secrets into Azure only if endpoints were newly created.
                </p>
              </div>
            )}

            {webhookBootstrapResult && !webhookBootstrapResult.success && (
              <div
                style={{
                  padding: "12px",
                  background: "#fee2e2",
                  border: "1px solid #fca5a5",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#991b1b",
                  marginTop: "8px",
                }}
              >
                <strong>❌ Webhook bootstrap failed:</strong>{" "}
                {webhookBootstrapResult.error}
              </div>
            )}

            <p
              style={{ fontSize: "11px", color: "#166534", margin: "8px 0 0" }}
            >
              ✅ <strong>Booth Renter prices</strong> are auto-created by
              bootstrap and kept in sync with the "Booth Renter Fee for
              Businesses" value in the settings above.
            </p>
          </div>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "#eff6ff",
          border: "1px solid #3b82f6",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#1e40af",
        }}
      >
        <strong>ℹ️ Important Notes:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
          <li>Subscription model changes affect new sign-ups immediately</li>
          <li>
            Existing subscriptions continue with their current model until
            renewal
          </li>
          <li>
            Grace period prevents immediate service disruption on payment
            failures
          </li>
          <li>
            Model switch lock prevents gaming the system with frequent switches
          </li>
        </ul>
      </div>
    </form>
  );
};

export default SubscriptionSettings;
